import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { useParams } from 'react-router-dom'
import {
  GoogleMap,
  MarkerClusterer,
  useJsApiLoader,
  GroundOverlay,
} from '@react-google-maps/api'
import type { Libraries } from '@googlemaps/js-api-loader'
import { Clusterer, type ClusterIconStyle } from '@react-google-maps/marker-clusterer'
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap'
import { GOOGLE_MAP_API_KEY, GOOGLE_MAP_CLUSTER_MAX_ZOOM, HOSTNAME } from '../../constants/Constants'
import './biz_plus.css'
import { getFromNowAsText } from '../../helpers/timeHelper'
import { GroupNavi, HeaderNavi } from '../navigation'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { getMamoriosWithLocation } from '../../helpers/CommonMamorioHelper'
import {
  calculateMamorioClusterIcon,
  groupMamoriosByPosition,
} from '../../helpers/mapMarkerHelper'
import MamorioMarker from '../common/MamorioMarker'
import OverlappingMamoriosMarker from '../common/OverlappingMamoriosMarker'
import BaseModal from '../common/BaseModal'
import '../overview/rental-label.css'
import {
  getTeamMamorios,
  getGroupMamorio,
  getGroupMamorios,
  Filter,
  SortOrder,
  SortOrderAsc,
  SortOrderDesc,
} from '../../modules/Mamorio'
import { getErrorString } from '../../utils/errorUtils'
import Loading from '../common/Loading'
import { selectLoadingState } from '../../store'
import MamorioListOnHeatmap from '../settings/mamorio/MamorioListOnBizPlus'
import { toId } from '../../utils/commonUtils'
import RentalModal from '../overview/RentalModal'
import ReloadButton from '../overview/ReloadButton'
import LendMamorioButton from '../overview/LendMamorioButton'
import { showModal } from '../../modules/UI'
import HeaderType from '../../modules/common/headerType'
import ChannelService from '../../modules/ChannelService'
import incrementGoogleMapsLoadCount from '../../utils/googleMapsLoadCounter'
import BlueprintSelectorModal from './BlueprintSelectorModal'
import DeckGlHeatmapLayer from './DeckGlHeatmapLayer'
import GroupMamorioCount from '../overview/GroupMamorioCount'
import { LastEventLogSortKey } from '../../helpers/lastEventLogHelper'
import updateBlueprintName from '../../modules/Blueprint'
// import AutoReloadModal from './AutoReloadModal'

type Position = {
  lat: number
  lng: number
}

type MapBounds = {
  north: number
  south: number
  east: number
  west: number
}

export type HeatmapFilter = {
  rssiInverted?: number
  precision?: number
}

const clusterStyles: ClusterIconStyle[] = [{
  url: 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m1.png',
  height: 53,
  width: 53,
  textColor: '#000000',
}]
const AUTO_RELOAD_MIN_SECONDS = 10
const DEFAULT_AUTO_RELOAD_SECONDS = 30
const JST_UTC_OFFSET_HOURS = 9
const JST_UTC_OFFSET_MS = JST_UTC_OFFSET_HOURS * 60 * 60 * 1000

const getDetectedDeviceLabel = (device: IDevice): string => device.name || `ID: ${device.id}`

const compareDetectedDevicesByName = (a: IDevice, b: IDevice): number => (
  getDetectedDeviceLabel(a).localeCompare(getDetectedDeviceLabel(b), 'en', {
    numeric: true,
    sensitivity: 'base',
  })
)

export const parseJstDateTimeLocal = (value: string): number | null => {
  if (!value) return null

  const matched = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/)
  if (!matched) return null

  const [, yearValue, monthValue, dayValue, hourValue, minuteValue, secondValue = '0'] = matched
  const year = Number(yearValue)
  const month = Number(monthValue)
  const day = Number(dayValue)
  const hour = Number(hourValue)
  const minute = Number(minuteValue)
  const second = Number(secondValue)
  const timestamp = Date.UTC(year, month - 1, day, hour - JST_UTC_OFFSET_HOURS, minute, second)
  const jstDate = new Date(timestamp + JST_UTC_OFFSET_MS)

  if (
    jstDate.getUTCFullYear() !== year
    || jstDate.getUTCMonth() !== month - 1
    || jstDate.getUTCDate() !== day
    || jstDate.getUTCHours() !== hour
    || jstDate.getUTCMinutes() !== minute
    || jstDate.getUTCSeconds() !== second
  ) {
    return null
  }

  return timestamp
}

const isDetectedAtOrAfter = (
  detectedAt: string | null | undefined,
  detectedAfterTimestamp: number | null,
): boolean => {
  if (detectedAfterTimestamp === null) return true
  if (!detectedAt) return false

  const detectedAtTimestamp = Date.parse(detectedAt)
  return !Number.isNaN(detectedAtTimestamp) && detectedAtTimestamp >= detectedAfterTimestamp
}

const isPositionInMapBounds = (
  position: Position,
  bounds: MapBounds | null,
): boolean => {
  if (!bounds) return true

  const isLatitudeInBounds = position.lat >= bounds.south && position.lat <= bounds.north
  const isLongitudeInBounds = bounds.west <= bounds.east
    ? position.lng >= bounds.west && position.lng <= bounds.east
    : position.lng >= bounds.west || position.lng <= bounds.east

  return isLatitudeInBounds && isLongitudeInBounds
}

const getMapBounds = (targetMap: google.maps.Map | null): MapBounds | null => {
  const bounds = targetMap?.getBounds()
  if (!bounds) return null

  const northEast = bounds.getNorthEast()
  const southWest = bounds.getSouthWest()

  return {
    north: northEast.lat(),
    south: southWest.lat(),
    east: northEast.lng(),
    west: southWest.lng(),
  }
}

type SortAndOrder = {
  sort?: string
  order?: SortOrder
}

const SortKey = {
  Name: 'name',
  Memo: 'memo',
  Time: undefined,
  LastDetectedDevice: 'last_detected_device',
  LastEventLogName: LastEventLogSortKey,
  BatteryLifeRessetedAt: 'battery_life_resetted_at',
  Status: 'status',
} as TSortKey

export const sanitizeLastDetectedDeviceFilter = (
  filter: Filter,
  isLastDetectedDeviceFilterEnabled: boolean,
): Filter => (
  isLastDetectedDeviceFilterEnabled
    ? filter
    : {
      ...filter,
      lastDetectedDeviceId: undefined,
      lastDetectedDeviceIds: undefined,
      lastDetectedDeviceName: undefined,
    }
)

export const calculateNextSort = (prevFilter: Filter, type: HeaderType): Filter => {
  const { sort, order } = prevFilter
  let next: SortAndOrder | null = null

  switch (type) {
    case HeaderType.Name:
      if (sort === SortKey.Time) {
        next = {
          sort: SortKey.Name,
          order: SortOrderAsc,
        }
        break
      }

      next = {
        sort: SortKey.Name,
        order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
      }
      break
    case HeaderType.Memo:
      if (sort === SortKey.Time) {
        next = {
          sort: SortKey.Memo,
          order: SortOrderAsc,
        }
        break
      }

      next = {
        sort: SortKey.Memo,
        order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
      }
      break
    case HeaderType.Time:
      if (sort === SortKey.Name) {
        next = {
          sort: SortKey.Time,
          order: SortOrderAsc,
        }
        break
      }
      next = {
        sort: SortKey.Time,
        order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
      }
      break
    case HeaderType.LastDetectedDevice:
      if (sort !== SortKey.LastDetectedDevice) {
        next = {
          sort: SortKey.LastDetectedDevice,
          order: SortOrderAsc,
        }
        break
      }
      next = {
        sort: SortKey.LastDetectedDevice,
        order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
      }
      break
    case HeaderType.LastEventLogName:
      if (sort !== SortKey.LastEventLogName) {
        next = {
          sort: SortKey.LastEventLogName,
          order: SortOrderAsc,
        }
        break
      }
      next = {
        sort: SortKey.LastEventLogName,
        order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
      }
      break
    case HeaderType.BatteryLifeRessetedAt:
      if (sort !== SortKey.BatteryLifeRessetedAt) {
        next = {
          sort: SortKey.BatteryLifeRessetedAt,
          order: SortOrderAsc,
        }
        break
      }
      next = {
        sort: SortKey.BatteryLifeRessetedAt,
        order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
      }
      break
    case HeaderType.Status:
      if (sort !== SortKey.Status) {
        next = {
          sort: SortKey.Status,
          order: SortOrderAsc,
        }
        break
      }
      next = {
        sort: SortKey.Status,
        order: order === SortOrderDesc ? SortOrderAsc : SortOrderDesc,
      }
      break
    default:
      // eslint-disable-next-line no-console
      console.warn(`${type} does not exist type`)
      return prevFilter
  }

  if (!next) {
    return prevFilter
  }

  return {
    ...prevFilter,
    ...next,
    page: 1,
  }
}

export const overviewTitleGroupIcon = 'data:image/png;base64,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'
const BizPlusPage = () => {
  const { group_id }: { group_id: string } = useParams()
  const dispatch = useAppDispatch()
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const group = useAppSelector((state) => (
    group_id
      ? state.team.groups?.find((_group) => String(_group.id) === group_id)
      : state.team.groups?.[0]
  ))
  const teamMamorioCount = useAppSelector((state) => state.team.mamorios_count)
  const rentalMode = useAppSelector((state) => state.team.rental_mode)
  const mamorios = useAppSelector((state) => state.mamorio.mamorios)
  const user = useAppSelector((state) => state.user)
  const canEditBlueprintNames = Boolean(group && user.user?.admin_groups?.some(
    (adminGroupId) => String(adminGroupId) === String(group.id),
  ))
  const memoEnabled = group?.memo_enabled ?? false
  const loading = useAppSelector(selectLoadingState)
  const [loadSelectedMamorio, setLoadSelectedMamorio] = useState(false)
  const [selectedMamorio, setSelectedMamorio] = useState<IMamorio | null>(null)
  const [showInfoWindow, setShowInfoWindow] = useState<boolean>(false) // InfoWindowの表示状態を管理
  const [openOverlappingPositionKey, setOpenOverlappingPositionKey] = useState<string | null>(null)
  const [heatmapData, setHeatmapData] = useState<google.maps.visualization.WeightedLocation[]>([])
  const [heatmapRaius, setHeatmapRadius] = useState<number>(20)
  const [mapRefreshToken, setMapRefreshToken] = useState(0)
  const [showOnlyMamoriosInMapBounds, setShowOnlyMamoriosInMapBounds] = useState(false)
  const [mapBounds, setMapBounds] = useState<MapBounds | null>(null)

  const [nameFilter, setNameFilter] = useState<string | null>(null)
  const [memoFilter, setMemoFilter] = useState<string | null>(null)
  const [lastDetectedDeviceIdsInput, setLastDetectedDeviceIdsInput] = useState<number[]>([])
  const [isLastDetectedDeviceDropdownOpen, setIsLastDetectedDeviceDropdownOpen] = useState(false)

  const defaultFilter = useMemo<Filter>(() => ({
    teamDomain: HOSTNAME?.split('.')[0],
    sort: 'name',
    order: SortOrderAsc as SortOrder,
    page: 1,
  }), [])

  const [filter, setFilter] = useState<Filter>(defaultFilter)
  const isLastDetectedDeviceFilterEnabled = group?.device_filter_enabled ?? false
  const sanitizedFilter = useMemo(
    () => sanitizeLastDetectedDeviceFilter(filter, isLastDetectedDeviceFilterEnabled),
    [filter, isLastDetectedDeviceFilterEnabled],
  )
  const groupBlueprints = useMemo<IBlueprint[]>(() => {
    if (group?.blueprints && group.blueprints.length > 0) {
      return group.blueprints
    }
    if (group?.blueprint) {
      return [group.blueprint]
    }
    return []
  }, [group?.blueprints, group?.blueprint])
  const [hiddenBlueprintIds, setHiddenBlueprintIds] = useState<Set<string>>(new Set())
  const blueprintStorageKey = useMemo(() => (
    group?.id ? `biz_plus_hidden_blueprint_ids_${group.id}` : null
  ), [group?.id])
  const autoReloadStorageKey = useMemo(() => (
    group?.id ? `biz_plus_auto_reload_${group.id}` : null
  ), [group?.id])
  const visibleBlueprints = useMemo<IBlueprint[]>(() => groupBlueprints.filter(
    (blueprint) => !hiddenBlueprintIds.has(String(blueprint.id)),
  ), [groupBlueprints, hiddenBlueprintIds])
  const availableDetectedDevices = useMemo<IDevice[]>(
    () => [...(group?.devices ?? [])].sort(compareDetectedDevicesByName),
    [group?.devices],
  )

  const loadFromLocalStorage = <T extends number | boolean | string>(key: string, defaultValue: T): T => {
    const value = localStorage.getItem(key)
    return value ? JSON.parse(value) : defaultValue
  }

  const [doRssiFilter, setDoRssiFilter] = useState<boolean>(loadFromLocalStorage('doRssiFilter', false))
  const [rssiInverted, setRssiInverted] = useState<number>(loadFromLocalStorage('rssiInverted', 100))
  const [doPrecisionFilter, setDoPrecisionFilter] = useState<boolean>(loadFromLocalStorage('doPrecisionFilter', true))
  const [precision, setPrecision] = useState<number>(loadFromLocalStorage('precision', 30))
  const [doDetectedAtFilter, setDoDetectedAtFilter] = useState<boolean>(loadFromLocalStorage('doDetectedAtFilter', false))
  const [detectedAfterDateTime, setDetectedAfterDateTime] = useState<string>(loadFromLocalStorage('detectedAfterDateTime', ''))
  const [autoReloadEnabled, setAutoReloadEnabled] = useState<boolean>(false)
  const [autoReloadIntervalSeconds, setAutoReloadIntervalSeconds] = useState<number>(DEFAULT_AUTO_RELOAD_SECONDS)
  const detectedAfterTimestamp = useMemo(
    () => (doDetectedAtFilter ? parseJstDateTimeLocal(detectedAfterDateTime) : null),
    [detectedAfterDateTime, doDetectedAtFilter],
  )

  const [mapCenter, setMapCenter] = useState<Position>({
    lat: Number(35.7007505),
    lng: Number(139.769443),
  })
  const [zoom] = useState(GOOGLE_MAP_CLUSTER_MAX_ZOOM)
  const [libraries] = useState<Libraries>(['visualization'])
  const {
    isLoaded,
    loadError,
  } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
    libraries,
  })
  const hasCountedLoadRef = useRef(false)
  const [size, setSize] = useState<undefined | google.maps.Size>(undefined)
  // const iconCacheRef = useRef<Map<string, string>>(new Map())
  const infoWindowOptions = {
    pixelOffset: size,
  }
  const updateOffsetSize = () => setSize(new window.google.maps.Size(0, 0))
  const updateMapBounds = useCallback((targetMap: google.maps.Map | null = map) => {
    const nextBounds = getMapBounds(targetMap)
    if (nextBounds) {
      setMapBounds(nextBounds)
    }
  }, [map])
  const updateHiddenBlueprintIds = useCallback((updater: (prev: Set<string>) => Set<string>) => {
    setHiddenBlueprintIds((prev) => updater(new Set(prev)))
  }, [])
  const normalizeAutoReloadSeconds = useCallback((value: number) => (
    Math.max(AUTO_RELOAD_MIN_SECONDS, Math.floor(value))
  ), [])

  const filterLogs = useCallback((mamorio: IMamorio): google.maps.visualization.WeightedLocation[] => [...mamorio.region_histories]
    .sort((a, b) => Date.parse(b.detected_at) - Date.parse(a.detected_at))
    .filter((h) => isDetectedAtOrAfter(h.detected_at, detectedAfterTimestamp))
    .filter((h) => doPrecisionFilter === false || !precision || (precision && h.precision && Number(h.precision) <= precision))
    .filter((h) => doRssiFilter === false || !rssiInverted || (rssiInverted && h.rssi && Number(h.rssi) >= -rssiInverted && Number(h.rssi) !== 0.0))
    .map((h) => ({
      location: new google.maps.LatLng({
        lat: Number(h.latitude),
        lng: Number(h.longitude),
      }),
      weight: 1.0,
    })), [detectedAfterTimestamp, doPrecisionFilter, doRssiFilter, precision, rssiInverted])

  const recentPosition = useCallback((mamorio: IMamorio): Position | null | undefined => {
    const recentLog = mamorio.recent_log
    if (
      !recentLog
      || !isDetectedAtOrAfter(recentLog.at, detectedAfterTimestamp)
      || (doRssiFilter && (!recentLog.rssi || (Number(recentLog.rssi) <= -rssiInverted || Number(recentLog.rssi) === 0.0)))
      || (doPrecisionFilter && (!recentLog.precision || Number(recentLog.precision) > precision))
    ) {
      return null
    }

    const position = {
      lat: Number(recentLog.latitude),
      lng: Number(recentLog.longitude),
    }
    return position
  }, [detectedAfterTimestamp, doRssiFilter, doPrecisionFilter, rssiInverted, precision])

  const recentDetectedAt = useCallback((mamorio: IMamorio): string => {
    const recentLog = mamorio.recent_log
    if (!recentLog) return ''
    return getFromNowAsText(recentLog.at)
  }, [])

  const selectMamorioOnMap = useCallback(async (mamorio: IMamorio) => {
    setOpenOverlappingPositionKey(null)
    setLoadSelectedMamorio(true)
    try {
      const updatedMamorio = await dispatch(getGroupMamorio({ mamorio_id: mamorio.id, group_id: mamorio.group_id })).unwrap()
      if (updatedMamorio) {
        setSelectedMamorio(updatedMamorio.mamorio)
      } else {
        setSelectedMamorio(mamorio)
      }
      setShowInfoWindow(true)
    } finally {
      setLoadSelectedMamorio(false)
    }
  }, [dispatch])

  function rssiStrength(): string {
    if (rssiInverted > 76) {
      return '弱い'
    }
    if (rssiInverted > 53) {
      return '中程度'
    }
    return '強い'
  }

  useEffect(() => {
    if (!selectedMamorio || !showInfoWindow) {
      setHeatmapData([])
      return
    }
    const filteredData = filterLogs(selectedMamorio)
    setHeatmapData(filteredData)
    if (filteredData[0] && filteredData[0].location) {
      setMapCenter({
        lat: filteredData[0].location.lat(),
        lng: filteredData[0].location.lng(),
      })
    }
  }, [selectedMamorio, showInfoWindow, doPrecisionFilter, doRssiFilter, precision, rssiInverted, nameFilter, memoFilter, filterLogs])

  const fetchMamorios = useCallback(async (groupId: string | undefined, filterArg: Filter) => {
    try {
      let result
      if (groupId) {
        result = await dispatch(getGroupMamorios({
          groupId: toId(groupId),
          filter: filterArg,
        })).unwrap()
      } else {
        result = await dispatch(getTeamMamorios({
          filter: filterArg,
        })).unwrap()
      }

      if (!result.mamorios) return false
      const mamoriosHasLocation = getMamoriosWithLocation(result.mamorios) as IMamorio[]
      const recentLog = mamoriosHasLocation[0]?.recent_log
      if (!recentLog) return false

      setMapCenter({
        lat: Number(recentLog.latitude),
        lng: Number(recentLog.longitude),
      })
      return true
    } catch (error: unknown) {
      // TODO: 例外処理
      console.error(getErrorString(error))
      return false
    }
  }, [dispatch])

  const showRentalModal = useCallback(() => {
    if (!group) return

    dispatch(showModal({
      modalType: BaseModal,
      modalProps: {
        title: 'このグループのMAMORIOを貸し出す',
        onActionConfirmed: undefined,
        children: <RentalModal group={group} />,
      },
    }))
  }, [dispatch, group])

  const handleToggleBlueprint = useCallback((blueprintId: string, shouldShow: boolean) => {
    updateHiddenBlueprintIds((next) => {
      if (shouldShow) {
        next.delete(blueprintId)
      } else {
        next.add(blueprintId)
      }
      return next
    })
  }, [updateHiddenBlueprintIds])

  const handleUpdateBlueprintName = useCallback(async (
    blueprintId: GeneralId,
    name: string | null,
  ): Promise<IBlueprint> => {
    if (!group || !canEditBlueprintNames) {
      throw new Error('図面名を編集する権限がありません。')
    }

    return dispatch(updateBlueprintName({
      groupId: group.id,
      blueprintId,
      name,
    })).unwrap()
  }, [canEditBlueprintNames, dispatch, group])

  const showBlueprintSelectorModal = useCallback(() => {
    if (groupBlueprints.length === 0) return

    dispatch(showModal({
      modalType: BaseModal,
      modalProps: {
        title: '図面を切り替える',
        onActionConfirmed: undefined,
        children: (
          <BlueprintSelectorModal
            blueprints={groupBlueprints}
            hiddenBlueprintIds={hiddenBlueprintIds}
            canEdit={canEditBlueprintNames}
            onToggleBlueprint={handleToggleBlueprint}
            onUpdateBlueprintName={handleUpdateBlueprintName}
          />
        ),
      },
    }))
  }, [
    canEditBlueprintNames,
    dispatch,
    groupBlueprints,
    handleToggleBlueprint,
    handleUpdateBlueprintName,
    hiddenBlueprintIds,
  ])

  /*
  const showAutoReloadModal = useCallback(() => {
    dispatch(showModal({
      modalType: BaseModal,
      modalProps: {
        title: '自動更新',
        onActionConfirmed: undefined,
        children: (
          <AutoReloadModal
            enabled={autoReloadEnabled}
            intervalSeconds={autoReloadIntervalSeconds}
            minSeconds={AUTO_RELOAD_MIN_SECONDS}
            onToggle={setAutoReloadEnabled}
            onIntervalChange={(value) => setAutoReloadIntervalSeconds(normalizeAutoReloadSeconds(value))}
          />
        ),
      },
    }))
  }, [autoReloadEnabled, autoReloadIntervalSeconds, dispatch, normalizeAutoReloadSeconds])
  */

  const runReload = useCallback(async () => {
    setSelectedMamorio(null)
    setShowInfoWindow(false)
    setOpenOverlappingPositionKey(null)
    setHeatmapData([])
    setLoadSelectedMamorio(false)
    setMapRefreshToken((prev) => prev + 1)
    const didReload = await fetchMamorios(group_id, sanitizedFilter)
    return didReload
  }, [fetchMamorios, group_id, sanitizedFilter])

  const handleReload = useCallback(async () => {
    await runReload()
  }, [runReload])

  const handleAutoReload = useCallback(async () => {
    await runReload()
  }, [runReload])

  const updateSort = useCallback((type: HeaderType) => {
    setFilter((prevFilter) => calculateNextSort(prevFilter, type))
  }, [])

  const updateLastDetectedDeviceIds = useCallback((deviceIds: number[]) => {
    const normalizedDeviceIds = [...new Set(deviceIds)]
      .filter((deviceId) => Number.isInteger(deviceId) && deviceId > 0)

    setLastDetectedDeviceIdsInput(normalizedDeviceIds)
    setFilter((prev) => ({
      ...prev,
      page: 1,
      lastDetectedDeviceId: normalizedDeviceIds.length === 1 ? normalizedDeviceIds[0] : undefined,
      lastDetectedDeviceIds: normalizedDeviceIds.length > 1 ? normalizedDeviceIds : undefined,
    }))
  }, [])

  const handleLastDetectedDeviceToggle = useCallback((deviceId: number) => {
    updateLastDetectedDeviceIds(
      lastDetectedDeviceIdsInput.includes(deviceId)
        ? lastDetectedDeviceIdsInput.filter((selectedDeviceId) => selectedDeviceId !== deviceId)
        : [...lastDetectedDeviceIdsInput, deviceId],
    )
  }, [lastDetectedDeviceIdsInput, updateLastDetectedDeviceIds])

  const selectedDetectedDeviceLabel = useMemo(() => {
    if (lastDetectedDeviceIdsInput.length === 0) {
      return 'すべて'
    }
    if (lastDetectedDeviceIdsInput.length > 1) {
      return `${lastDetectedDeviceIdsInput.length}件選択`
    }
    const selectedDevice = availableDetectedDevices.find(
      (device) => Number(device.id) === lastDetectedDeviceIdsInput[0],
    )
    return selectedDevice ? getDetectedDeviceLabel(selectedDevice) : `ID: ${lastDetectedDeviceIdsInput[0]}`
  }, [availableDetectedDevices, lastDetectedDeviceIdsInput])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (typeof window.ChannelIO === 'undefined') {
      ChannelService.bootWithMember(user.user?.id)
    }
  }, [user.user?.id])

  useEffect(() => {
    fetchMamorios(
      group_id,
      sanitizedFilter,
    )
  }, [fetchMamorios, group_id, sanitizedFilter])

  useEffect(() => {
    if (!blueprintStorageKey) {
      setHiddenBlueprintIds(new Set())
      return
    }

    const stored = localStorage.getItem(blueprintStorageKey)
    if (!stored) {
      setHiddenBlueprintIds(new Set())
      return
    }

    try {
      const parsed = JSON.parse(stored)
      const availableIds = new Set(groupBlueprints.map((blueprint) => String(blueprint.id)))
      if (Array.isArray(parsed)) {
        setHiddenBlueprintIds(new Set(parsed
          .map((id) => String(id))
          .filter((id) => availableIds.has(id))))
      } else {
        setHiddenBlueprintIds(new Set())
      }
    } catch (error) {
      setHiddenBlueprintIds(new Set())
    }
  }, [blueprintStorageKey, groupBlueprints])

  useEffect(() => {
    if (!autoReloadStorageKey) {
      setAutoReloadEnabled(false)
      setAutoReloadIntervalSeconds(DEFAULT_AUTO_RELOAD_SECONDS)
      return
    }

    const stored = localStorage.getItem(autoReloadStorageKey)
    if (!stored) {
      setAutoReloadEnabled(false)
      setAutoReloadIntervalSeconds(DEFAULT_AUTO_RELOAD_SECONDS)
      return
    }

    try {
      const parsed = JSON.parse(stored)
      if (typeof parsed === 'object' && parsed) {
        const storedEnabled = Boolean(parsed.enabled)
        const storedInterval = typeof parsed.intervalSeconds === 'number'
          ? normalizeAutoReloadSeconds(parsed.intervalSeconds)
          : DEFAULT_AUTO_RELOAD_SECONDS
        setAutoReloadEnabled(storedEnabled)
        setAutoReloadIntervalSeconds(storedInterval)
      } else {
        setAutoReloadEnabled(false)
        setAutoReloadIntervalSeconds(DEFAULT_AUTO_RELOAD_SECONDS)
      }
    } catch (error) {
      setAutoReloadEnabled(false)
      setAutoReloadIntervalSeconds(DEFAULT_AUTO_RELOAD_SECONDS)
    }
  }, [autoReloadStorageKey, normalizeAutoReloadSeconds])

  useEffect(() => {
    setSelectedMamorio(null)
    setShowInfoWindow(false)
    setOpenOverlappingPositionKey(null)
    setHeatmapData([])
    setLoadSelectedMamorio(false)
    setIsLastDetectedDeviceDropdownOpen(false)
    setLastDetectedDeviceIdsInput([])
    setFilter((prev) => {
      const next = {
        ...sanitizeLastDetectedDeviceFilter(prev, false),
        page: 1,
      }

      return (
        prev.page === next.page
        && prev.lastDetectedDeviceId === next.lastDetectedDeviceId
        && prev.lastDetectedDeviceIds === next.lastDetectedDeviceIds
        && prev.lastDetectedDeviceName === next.lastDetectedDeviceName
      ) ? prev : next
    })
  }, [group_id])

  useEffect(() => {
    if (isLastDetectedDeviceFilterEnabled) return

    setIsLastDetectedDeviceDropdownOpen(false)

    const hasLastDetectedDeviceFilter = (
      lastDetectedDeviceIdsInput.length > 0
      || typeof filter.lastDetectedDeviceId !== 'undefined'
      || typeof filter.lastDetectedDeviceIds !== 'undefined'
      || typeof filter.lastDetectedDeviceName !== 'undefined'
    )

    if (!hasLastDetectedDeviceFilter) return

    setLastDetectedDeviceIdsInput([])
    setFilter((prev) => ({
      ...sanitizeLastDetectedDeviceFilter(prev, false),
      page: 1,
    }))
  }, [
    filter.lastDetectedDeviceId,
    filter.lastDetectedDeviceIds,
    filter.lastDetectedDeviceName,
    isLastDetectedDeviceFilterEnabled,
    lastDetectedDeviceIdsInput,
  ])

  useEffect(() => {
    const saveToLocalStorage = () => {
      localStorage.setItem('doRssiFilter', JSON.stringify(doRssiFilter))
      localStorage.setItem('rssiInverted', JSON.stringify(rssiInverted))
      localStorage.setItem('doPrecisionFilter', JSON.stringify(doPrecisionFilter))
      localStorage.setItem('precision', JSON.stringify(precision))
      localStorage.setItem('doDetectedAtFilter', JSON.stringify(doDetectedAtFilter))
      localStorage.setItem('detectedAfterDateTime', JSON.stringify(detectedAfterDateTime))
    }
    saveToLocalStorage()
  }, [doRssiFilter, rssiInverted, doPrecisionFilter, precision, doDetectedAtFilter, detectedAfterDateTime])

  useEffect(() => {
    if (!isLoaded || hasCountedLoadRef.current) {
      return
    }
    incrementGoogleMapsLoadCount('BizPlusPage')
    hasCountedLoadRef.current = true
  }, [isLoaded])
  useEffect(() => {
    if (!blueprintStorageKey) return
    localStorage.setItem(blueprintStorageKey, JSON.stringify([...hiddenBlueprintIds]))
  }, [blueprintStorageKey, hiddenBlueprintIds])

  useEffect(() => {
    if (!autoReloadStorageKey) return
    localStorage.setItem(autoReloadStorageKey, JSON.stringify({
      enabled: autoReloadEnabled,
      intervalSeconds: autoReloadIntervalSeconds,
    }))
  }, [autoReloadEnabled, autoReloadIntervalSeconds, autoReloadStorageKey])

  useEffect(() => {
    if (!autoReloadEnabled) return undefined
    const intervalId = window.setInterval(() => {
      handleAutoReload()
    }, autoReloadIntervalSeconds * 1000)
    return () => window.clearInterval(intervalId)
  }, [autoReloadEnabled, autoReloadIntervalSeconds, handleAutoReload])

  const filteredMamoriosForMap = useMemo(() => (
    mamorios
      ?.filter((mamorio) => !showInfoWindow || selectedMamorio?.id === mamorio.id)
      .filter((mamorio) => nameFilter === null || nameFilter === '' || mamorio.name.includes(nameFilter))
      .filter((mamorio) => memoFilter === null || memoFilter === '' || mamorio.memo?.includes(memoFilter))
      .filter((mamorio) => recentPosition(mamorio) != null)
  ) ?? [], [
    mamorios,
    memoFilter,
    nameFilter,
    recentPosition,
    selectedMamorio?.id,
    showInfoWindow,
  ])

  const filteredMamoriosInMapBounds = useMemo(() => (
    filteredMamoriosForMap.filter((mamorio) => {
      const position = recentPosition(mamorio)
      return position ? isPositionInMapBounds(position, mapBounds) : false
    })
  ), [filteredMamoriosForMap, mapBounds, recentPosition])

  const mamorioPositionGroups = useMemo(() => (
    groupMamoriosByPosition(filteredMamoriosForMap, recentPosition)
  ), [filteredMamoriosForMap, recentPosition])

  const clusters = (clusterer: Clusterer): JSX.Element[] => mamorioPositionGroups.map((positionGroup) => {
    if (positionGroup.mamorios.length > 1) {
      return (
        <OverlappingMamoriosMarker
          key={`overlapping-mamorios-${positionGroup.positionKey}`}
          clusterer={clusterer}
          mamorios={positionGroup.mamorios}
          position={positionGroup.position}
          isOpen={openOverlappingPositionKey === positionGroup.positionKey}
          onOpen={() => {
            setSelectedMamorio(null)
            setShowInfoWindow(false)
            setOpenOverlappingPositionKey(positionGroup.positionKey)
          }}
          onClose={() => setOpenOverlappingPositionKey(null)}
          onMamorioSelect={selectMamorioOnMap}
          infoWindowOptions={infoWindowOptions}
          recentDetectedAt={recentDetectedAt}
          showMemo={memoEnabled}
        />
      )
    }

    const mamorio = positionGroup.mamorios[0]!
    return (
      <MamorioMarker
        key={mamorio.id}
        mamorio={mamorio}
        clusterer={clusterer}
        isSelected={selectedMamorio?.id === mamorio.id && showInfoWindow} // InfoWindow表示時のみ選択状態を表示
        onMarkerClick={async () => {
          setOpenOverlappingPositionKey(null)
          if (selectedMamorio?.id === mamorio.id) {
            // 同じマーカーをクリック: InfoWindowをトグル
            setShowInfoWindow(!showInfoWindow)
          } else {
            // 異なるマーカーをクリック: 選択を変更してInfoWindowを表示
            await selectMamorioOnMap(mamorio)
          }
        }}
        onInfoWindowClose={() => {
          // InfoWindowを閉じる: 選択状態は維持、全マーカーを表示
          setShowInfoWindow(false)
          setOpenOverlappingPositionKey(null)
        }}
        infoWindowOptions={infoWindowOptions}
        recentPosition={recentPosition}
        recentDetectedAt={recentDetectedAt}
        showMemo={memoEnabled}
      />
    )
  })
  // const autoReloadButtonText = autoReloadEnabled
  //   ? `自動更新中...（${autoReloadIntervalSeconds}秒）`
  //   : '自動更新'
  const renderMap = () => {
    const filteredMamorios = showOnlyMamoriosInMapBounds
      ? filteredMamoriosInMapBounds
      : filteredMamoriosForMap

    return (
      <div
        className="content heatmap"
        data-testid="overview"
      >
        <HeaderNavi />
        <Loading show={loading && !loadSelectedMamorio} />
        <GroupNavi>
          <>
            <div className="overview-title">
              <div className="overview-title__heading">
                <div className="group-name" data-testid="current-group-name">
                  <img src={overviewTitleGroupIcon} alt="group-icon" />
                  {group?.name ?? ''}
                </div>
                <GroupMamorioCount mamoriosCount={group_id ? group?.mamorios_count : teamMamorioCount} />
              </div>
              <div>
                <ReloadButton onClick={handleReload} />
                {group?.mode === 'rental' && (
                  <LendMamorioButton onClick={showRentalModal} />
                )}
              </div>
            </div>
            <div className="topmap">
              <GoogleMap
                zoom={zoom}
                mapContainerStyle={{ height: '445px' }}
                center={mapCenter}
                onLoad={(_map) => {
                  setMap(_map)
                  updateOffsetSize()
                  updateMapBounds(_map)
                }}
                onIdle={() => updateMapBounds()}
                onZoomChanged={() => {
                  const currentZoom = map?.getZoom()
                  if (currentZoom) {
                    const metersPerPx = (156543.03392 * Math.cos((25.0 * Math.PI) / 180)) / (2 ** currentZoom)
                    const bleRadius = 10.0 / metersPerPx
                    setHeatmapRadius(bleRadius)
                  }
                }}
              >
                {visibleBlueprints.map((blueprint) => (
                  <GroundOverlay
                    key={`${group?.id ?? 'group'}-${blueprint.id}`}
                    url={blueprint.image.url}
                    opacity={0.5}
                    bounds={{
                      north: blueprint.north,
                      south: blueprint.south,
                      east: blueprint.east,
                      west: blueprint.west,
                    }}
                  />
                ))}
                <MarkerClusterer
                  key={`clusterer-${mapRefreshToken}`}
                  averageCenter
                  calculator={calculateMamorioClusterIcon}
                  enableRetinaIcons
                  gridSize={60}
                  maxZoom={GOOGLE_MAP_CLUSTER_MAX_ZOOM}
                  styles={clusterStyles}
                >
                  {(clusterer) => (<>{clusters(clusterer)}</>)}
                </MarkerClusterer>
                <DeckGlHeatmapLayer
                  data={showInfoWindow ? heatmapData : []}
                  radius={heatmapRaius}
                />
              </GoogleMap>
              <div className="topmap-controls">
                <label className="topmap-visible-area-toggle">
                  <input
                    type="checkbox"
                    aria-label="表示中の地図範囲だけをリスト表示"
                    checked={showOnlyMamoriosInMapBounds}
                    onChange={(event) => setShowOnlyMamoriosInMapBounds(event.target.checked)}
                  />
                  <span>表示中の地図範囲だけをリスト表示</span>
                  {showOnlyMamoriosInMapBounds && (
                    <span className="topmap-visible-area-count">
                      {filteredMamoriosInMapBounds.length}
                      件
                    </span>
                  )}
                </label>
                {groupBlueprints.length > 0 && (
                  <button
                    className="topmap-control-button"
                    type="button"
                    onClick={showBlueprintSelectorModal}
                  >
                    図面を切り替える
                  </button>
                )}
                {/*
                  一時的に自動更新導線を非表示化。
                  復活時は下記ボタンブロックのコメントを外す。
                <button
                  className="topmap-control-button"
                  type="button"
                  onClick={showAutoReloadModal}
                >
                  {autoReloadButtonText}
                </button>
                */}
              </div>
              <div className="heatmapConfig" style={{ padding: '20px', display: 'flex' }}>
                <div style={{ width: '300px' }}>
                  <p>
                    名前で検索
                    <input
                      type="text"
                      onChange={(event) => setNameFilter(event.target.value)}
                    />
                  </p>
                  <p>
                    メモで検索
                    <input
                      type="text"
                      onChange={(event) => setMemoFilter(event.target.value)}
                    />
                  </p>
                  {isLastDetectedDeviceFilterEnabled && (
                    <p className="biz-plus-last-detected-device-filter">
                      <span className="biz-plus-last-detected-device-label">
                        最後に検知した端末
                      </span>
                      <Dropdown
                        isOpen={isLastDetectedDeviceDropdownOpen}
                        toggle={() => setIsLastDetectedDeviceDropdownOpen((prev) => !prev)}
                        data-testid="biz-plus-last-detected-device-dropdown"
                      >
                        <DropdownToggle
                          color="white"
                          className="biz-plus-last-detected-device-toggle"
                          data-testid="biz-plus-last-detected-device-dropdown-toggle"
                        >
                          <span className="biz-plus-last-detected-device-content">
                            <span className="biz-plus-last-detected-device-value">
                              {selectedDetectedDeviceLabel}
                            </span>
                            <span className="biz-plus-last-detected-device-caret" aria-hidden="true" />
                          </span>
                        </DropdownToggle>
                        <DropdownMenu data-testid="biz-plus-last-detected-device-dropdown-menu">
                          <DropdownItem
                            toggle={false}
                            onClick={() => {
                              updateLastDetectedDeviceIds([])
                              setIsLastDetectedDeviceDropdownOpen(false)
                            }}
                          >
                            <span className="biz-plus-last-detected-device-item-label">
                              すべて
                            </span>
                          </DropdownItem>
                          {availableDetectedDevices.map((device) => {
                            const deviceId = Number(device.id)
                            const isSelected = lastDetectedDeviceIdsInput.includes(deviceId)

                            return (
                              <label
                                className="dropdown-item biz-plus-last-detected-device-option"
                                key={device.id}
                              >
                                <input
                                  type="checkbox"
                                  className="biz-plus-last-detected-device-checkbox"
                                  checked={isSelected}
                                  onChange={() => handleLastDetectedDeviceToggle(deviceId)}
                                />
                                <span className="biz-plus-last-detected-device-item-label">
                                  {getDetectedDeviceLabel(device)}
                                </span>
                              </label>
                            )
                          })}
                        </DropdownMenu>
                      </Dropdown>
                    </p>
                  )}
                </div>
                <div style={{ width: '300px' }}>
                  <p style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={doRssiFilter}
                      style={{ width: '20px', marginRight: '6px' }}
                      onChange={(event) => setDoRssiFilter(event.target.checked)}
                    />
                    電波強度でフィルタリングする
                  </p>
                  <p>
                    電波が
                    {rssiStrength()}
                    (rssiが-
                    {rssiInverted}
                    )
                  </p>
                  <input
                    type="range"
                    value={rssiInverted}
                    style={{ maxWidth: '100%' }}
                    onChange={(event) => {
                      setRssiInverted(Number(event.target.value))
                    }}
                    min={30}
                    max={100}
                    step={1}
                    disabled={!doRssiFilter}
                  />
                  <p>
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={doDetectedAtFilter}
                        style={{ width: '20px', marginRight: '6px' }}
                        onChange={(event) => setDoDetectedAtFilter(event.target.checked)}
                      />
                      検知時刻でフィルタリングする
                    </label>
                  </p>
                  <p>指定時刻以後（JST）</p>
                  <input
                    type="datetime-local"
                    aria-label="検知基準時刻（JST）"
                    value={detectedAfterDateTime}
                    style={{ maxWidth: '100%' }}
                    onChange={(event) => setDetectedAfterDateTime(event.target.value)}
                    disabled={!doDetectedAtFilter}
                  />
                </div>
                <div style={{ width: '300px' }}>
                  <p style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={doPrecisionFilter}
                      style={{ width: '20px', marginRight: '6px' }}
                      onChange={(event) => setDoPrecisionFilter(event.target.checked)}
                    />
                    GPS誤差でフィルタリングする
                  </p>
                  <p>
                    誤差が
                    {precision}
                    m以内
                  </p>
                  <input
                    type="range"
                    value={precision}
                    style={{ maxWidth: '100%' }}
                    onChange={(event) => setPrecision(Number(event.target.value))}
                    min={1}
                    max={1000}
                    step={1}
                    disabled={!doPrecisionFilter}
                  />
                </div>
              </div>
            </div>
            {
              group && (
                <MamorioListOnHeatmap
                  group={group}
                  mamorios={filteredMamorios}
                  filter={sanitizedFilter}
                  onChangeSort={updateSort}
                  rentalMode={rentalMode}
                />
              )
            }
          </>
        </GroupNavi>
      </div>
    )
  }

  if (loadError) {
    return <div>Map cannot be loaded right now, sorry.</div>
  }

  return isLoaded ? renderMap() : null
}

export default BizPlusPage
